import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Switch } from "../components/ui/Switch";
import { ArrowLeft, Save, UserCog, Shield } from "lucide-react";
import { adminsService } from "../services/admins";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";

interface NovoAdministradorFormProps {
  onBack: () => void;
}

export function NovoAdministradorForm({ onBack }: NovoAdministradorFormProps) {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { user: currentUser } = useOutletContext<any>() || { user: null };

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [originalActiveStatus, setOriginalActiveStatus] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    is_active: true,
  });

  useEffect(() => {
    if (isEdit && id) {
      const fetchAdmin = async () => {
        try {
          setLoading(true);
          const response = await adminsService.getById(id);
          const admin = response.data.data || response.data;
          setFormData({
            name: admin.name,
            email: admin.email,
            password: "",
            password_confirmation: "",
            is_active: admin.is_active,
          });
          setOriginalActiveStatus(admin.is_active);
        } catch (error) {
          console.error("Erro ao buscar administrador:", error);
          toast.error("Erro ao carregar dados do administrador.");
          onBack();
        } finally {
          setLoading(false);
        }
      };
      fetchAdmin();
    }
  }, [isEdit, id, onBack]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações básicas
    if (!formData.name.trim()) {
      toast.error("O nome é obrigatório.");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("O e-mail é obrigatório.");
      return;
    }

    // Na criação a senha é obrigatória. Na edição, é opcional.
    if (!isEdit && !formData.password) {
      toast.error("A senha é obrigatória para novos administradores.");
      return;
    }

    if (formData.password) {
      if (formData.password.length < 8) {
        toast.error("A senha deve conter no mínimo 8 caracteres.");
        return;
      }
      if (formData.password !== formData.password_confirmation) {
        toast.error("As senhas não coincidem.");
        return;
      }
    }

    try {
      setSubmitting(true);

      const payload: Record<string, any> = {
        name: formData.name,
        email: formData.email,
      };

      if (formData.password) {
        payload.password = formData.password;
        payload.password_confirmation = formData.password_confirmation;
      }

      if (isEdit && id) {
        // Atualiza dados cadastrais
        await adminsService.update(id, payload);

        // Se a flag ativo/inativo mudou, atualiza via endpoint correspondente
        if (formData.is_active !== originalActiveStatus) {
          if (formData.is_active) {
            await adminsService.activate(id);
          } else {
            await adminsService.deactivate(id);
          }
        }

        toast.success("Administrador atualizado com sucesso!");
      } else {
        // Criação de administrador
        await adminsService.create(payload);
        toast.success("Administrador criado com sucesso!");
      }

      onBack();
    } catch (error: any) {
      console.error("Erro ao salvar administrador:", error);
      const errorMsg = error.response?.data?.message || "Erro ao salvar os dados do administrador.";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const isSelf = currentUser && id && Number(id) === currentUser.id;

  if (loading) {
    return (
      <main className="flex-1 p-6 bg-gray-50 flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Administradores
        </Button>
        <h1>{isEdit ? "Editar Administrador" : "Novo Administrador"}</h1>
        <p className="text-gray-600">
          {isEdit ? "Atualize as informações do administrador" : "Cadastrar novo usuário administrador"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <UserCog className="h-5 w-5 text-gray-500" />
                Dados Principais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  placeholder="Nome do Administrador"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@foradocloset.com.br"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <Shield className="h-5 w-5 text-gray-500" />
                Acesso e Segurança
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="password">
                  {isEdit ? "Nova Senha (Opcional)" : "Senha"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={isEdit ? "Deixe em branco para não alterar" : "Mínimo 8 caracteres"}
                  required={!isEdit}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="password_confirmation">Confirmar Senha</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  placeholder={isEdit ? "Deixe em branco para não alterar" : "Confirme a senha"}
                  required={!isEdit}
                  value={formData.password_confirmation}
                  onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                />
              </div>

              {isEdit && (
                <div className="pt-4 border-t flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_active">Status da Conta</Label>
                    <p className="text-xs text-gray-500">
                      {isSelf 
                        ? "Você não pode desativar o seu próprio acesso." 
                        : "Desative para bloquear temporariamente o acesso."
                      }
                    </p>
                  </div>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    disabled={isSelf}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-4 pt-4">
          <Button type="submit" className="flex-1" disabled={submitting}>
            <Save className="h-4 w-4 mr-2" />
            {submitting ? "Salvando..." : isEdit ? "Salvar Alterações" : "Cadastrar Administrador"}
          </Button>
          <Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
            Cancelar
          </Button>
        </div>
      </form>
    </main>
  );
}

export default NovoAdministradorForm;
